import React, { HTMLAttributes, ReactNode } from 'react';

import classNames from 'classnames';
import escapeRegExp from 'lodash/escapeRegExp';

import { Box } from '../../../../../Box';
import { Badge } from '../../../../common';
import { Typography } from '../../../../../Typography';
import { SelectInputItem } from '../../../types';
import useStyles from '../../../SelectInput.styles';

interface OptionProps<T> {
  inputValue: string;
  renderOption: ((option: SelectInputItem<T>) => ReactNode) | undefined;
  optionProps: HTMLAttributes<HTMLLIElement>;
  option: SelectInputItem<T>;
}

export const Option = <T extends string | number>({
  inputValue,
  renderOption,
  optionProps,
  option,
}: OptionProps<T>) => {
  const { classes } = useStyles();

  if (renderOption) {
    return (
      <li {...optionProps} key={option.value}>
        {renderOption(option)}
      </li>
    );
  }

  const isBottom = option.badge?.position === 'bottom';
  const words = option.label.split(
    new RegExp(`(${escapeRegExp(inputValue)})`, 'gi'),
  );

  return (
    <li
      {...optionProps}
      key={option.value}
      className={classNames(classes.option, classes.menuItem, {
        [classes.defaultOption]: !option.badge,
      })}
    >
      <Box
        display="flex"
        alignItems={isBottom ? 'start' : 'center'}
        flexDirection={isBottom ? 'column' : 'row'}
        gap="8"
        pointerEvents={option.disabled ? 'none' : 'auto'}
      >
        {option.badge && (
          <Box order={isBottom ? 1 : 0}>
            <Badge
              title={option.badge.title}
              description={option.badge.description}
              color={option.badge.color}
            />
          </Box>
        )}
        <Box order={isBottom ? 0 : 1}>
          <Typography
            component="span"
            variant="body3"
            color={inputValue ? 'text.light.tertirary' : 'text.light.primary'}
          >
            {inputValue &&
              words.map((part, i) => (
                <Typography
                  key={i}
                  component="span"
                  variant="body3"
                  color={
                    part.toLowerCase() === inputValue.toLowerCase()
                      ? 'text.light.primary'
                      : 'text.light.tertirary'
                  }
                >
                  {part}
                </Typography>
              ))}
            {!inputValue && option.label}
          </Typography>
        </Box>
      </Box>
    </li>
  );
};
